import { Component, Vue, Watch } from "vue-property-decorator";
import {
	ApplicationViews,
	NonCurriculumComponentType,
	ParentType,
	VcsMenuCategory,
	VcsMenuCategoryPermission,
	CurriculumType
} from "@/util/enum";
import { LanguageSkillModel, VCSLevelModel } from "@/models/title-list-model";
import { Paths } from "@/util/paths";
import { MessageHelper, NotificationType, fmtMsg, GLUtil } from "commonui";
import { getModule } from "vuex-module-decorators";
import TitleListState from "@/modules/title-list";
import {
	disabledDueToAccessLevel,
	getRouteAccessLevel,
	getUniqueChangedWords,
	isAnyVersionSelected,
	isLinkVisible
} from "@/util/func";
import { Alert, Spin, Button, Modal, Radio } from "ant-design-vue";
import { VersionGroupModule } from "@/modules/versiongroup";
import {
	ContentTextEditModel,
	CurrentContentDetailsForm,
	EditContentRequestModel,
	EditContentTextRequestModel,
	CreateContentRequestModel,
	ContentService,
	ContentTextModel
} from "@/services/content";
import { TitleListLocale } from "@/locales/localeid";
import { max, sum } from "lodash";
import PageTitle from "@/components/page-title/page-title.vue";

enum DisabledTabType {
	create = 1,
	edit = 2,
	textDirty = 3,
	all = 4
}

@Component({
	components: {
		ChildNavigation: () =>
			import(/* webpackPrefetch: true */ "@/components/navigation/child-navigation/child-navigation.vue"),
		CommonForm: () => import(/* webpackPrefetch: true */ "./sub-views/common-form/common-form.vue"),
		List: () => import(/* webpackPreload: true */ "./sub-views/list/list.vue"),
		VersionComparer: () => import(/* webpackPrefetch: true */ "./sub-views/version-comparer/version-comparer.vue"),
		Radio,
		RadioGroup: Radio.Group,
		Alert,
		Spin,
		Button,
		Modal,
		PageTitle
	}
})
export default class TitleList extends Vue {
	public view = ApplicationViews.NonCurriculumComponents;
	private titleListModule = getModule(TitleListState);
	private versionGroupModule = VersionGroupModule; // For watch need to create local reference
	// public languageSkills: LanguageSkillModel[] = [];
	public levels: VCSLevelModel[] = [];
	public loadList = false;
	public disabledTabs: Paths[] = [];
	public noVersion: boolean = false;
	public saving = false;
	public shouldReset = false;
	public showComparer = false;
	fmtMsg = fmtMsg;
	locale = TitleListLocale;
	private oldTextLines: string[] = [];
	private newTextLines: string[] = [];
	public stickyFooterWidth = 500;
	public stickyDefault = { height: 50, bottom: 70 };
	public stickyFooterHeight = this.stickyDefault.height;
	public stickyFooterBottomPos = this.stickyDefault.bottom;
	public stickyFooterRight = 10;

	get isListPath() {
		return this.$route.path === Paths.NonCurriculumComponents;
	}

	get showSubmit() {
		return (
			this.$route.path === Paths.NonCurriculumComponentsText ||
			this.$route.matched.slice(-1)[0].path === Paths.NonCurriculumComponentsEditText
		);
	}

	get currentRouteAccessLevel() {
		return getRouteAccessLevel(this.$route);
	}

	get disabledDueToAccessLevel() {
		return disabledDueToAccessLevel(this.currentRouteAccessLevel);
	}

	@Watch("titleListModule.updateTitleInfo")
	updateTitleInfo() {
		if (this.titleListModule.updateTitleInfo) {
			this.execUpdateTitleInfo();
			this.titleListModule.setUpdateTitleInfo(false);
		}
	}

	@Watch("$route.path")
	addStickyListener() {
		Vue.nextTick(() => this.addChildWidthChangeEventListener());
	}

	@Watch("titleListModule.editedContentDetails.parentDontContainsContent", { immediate: true })
	goToHome() {
		// Means version is changed and contentId is not of parent version
		if (this.titleListModule.editedContentDetails.parentDontContainsContent) {
			this.goToParent();
		}
	}

	updateDisabledTabsToManage() {
		if (this.titleListModule.editeContentSelectedChapterId !== "all" && this.$route.params.id) {
			// none means normal content, so update disabled as per edit, else if chapter selected, update disabled as per chapter.
			if (this.titleListModule.editeContentSelectedChapterId === "none") {
				this.updateDisabledTabs(DisabledTabType.edit);
			}
		}

		/* 
		// Set the oldTextLine to []. 
		// This is done since on switching chapter and adding or editing words in text of the chapter 
		// the comparison was done with the text of old chapter. 
		*/
		this.oldTextLines = [];
	}

	@Watch("versionGroupModule.selectedGsVersion", { immediate: true })
	GsVersionUpdatedIm() {
		if (!isAnyVersionSelected()) {
			this.noVersion = true;
			this.goToParent();
		} else {
			this.noVersion = false;
		}
	}

	@Watch("versionGroupModule.selectedGsVersion")
	GsVersionUpdated() {
		if (isAnyVersionSelected() && !this.showComparer) {
			this.reFetchDetails();
		}
	}

	@Watch("versionGroupModule.selectedLsVersion")
	LsVersionUpdated() {
		if (!isAnyVersionSelected()) {
			this.noVersion = true;
			this.goToParent();
		} else {
			this.noVersion = false;
			!this.showComparer && this.reFetchDetails();
		}
	}

	@Watch("versionGroupModule.disabled")
	enableVersionBar() {
		if (this.versionGroupModule.disabled) {
			this.versionGroupModule.setDisabled(false);
		}
	}

	created() {
		this.versionGroupModule.setDisabled(false);
		if (!isAnyVersionSelected()) {
			this.noVersion = true;
		}
		if (Array.isArray(this.titleListModule.levels)) {
			this.levels = this.titleListModule.levels;
		}
		if (this.$route.matched.slice(-1)[0].path === Paths.NonCurriculumComponents) {
			// reset form and title list and isDirty
			this.titleListModule.resetChildState();
		}

		// handle page refresh for subrouting
		if (this.$route.params.id) {
			this.updateDisabledTabs(DisabledTabType.edit);
		} else if (this.$route.matched.slice(-1)[0].path !== Paths.NonCurriculumComponents) {
			this.updateDisabledTabs(DisabledTabType.create);
		} else {
			this.disabledTabs = [];
		}
	}

	mounted() {
		this.addChildWidthChangeEventListener();
		this.updateDisabledTabsToManage();
	}

	updated() {
		// reset store when user come back from child routes
		if (this.$route.matched.slice(-1)[0].path === Paths.NonCurriculumComponents) {
			if (this.shouldReset) {
				this.titleListModule.resetChildState();
				this.showComparer = false;
				this.shouldReset = false;
			}
		} else if (!this.shouldReset) {
			this.shouldReset = true;
		}
	}

	destroyed() {
		this.versionGroupModule.setDisabled(true);
	}

	addChildWidthChangeEventListener() {
		const text: Vue = this.$refs.nav as any;
		const platform = navigator && navigator.platform ? navigator.platform : "";
		const isWindow = /^win/g.test(platform.toLowerCase());
		if (text) {
			const setWidth = () => {
				// check if scroll is available on windows.
				if (isWindow) {
					const el: HTMLCollectionOf<Element> = document.getElementsByClassName("vc-layout__content");
					if (el && el.length) {
						const children = Array.from(el[0].children);
						const childHeight = sum(children.map((m) => m.clientHeight)) + 9;
						if (childHeight > el[0].clientHeight) {
							this.stickyFooterRight = 27;
						} else {
							this.stickyFooterRight = 10;
						}
					}
				}
				this.stickyFooterWidth = text.$children[0].$el.clientWidth + 40;
			};
			setWidth();
			const win = window as any;
			new win.ResizeObserver(setWidth).observe(text.$children[0].$el);

			if (isWindow) {
				new win.ResizeObserver(setWidth).observe(document.getElementsByClassName("vc-layout__content")[0]);
			}
		}
	}

	reFetchDetails() {
		if (this.titleListModule.editedContentId.length) {
			this.titleListModule.fetchContentDetails(this.titleListModule.editedContentId);
		}
	}

	goToParent() {
		if (this.$route.matched.slice(-1)[0].path != Paths.NonCurriculumComponents) {
			this.$router.replace(Paths.NonCurriculumComponents);
			this.titleListModule.resetChildState();
		}
	}

	updateDisabledTabs(type: DisabledTabType) {
		switch (type) {
			case DisabledTabType.create:
				this.disabledTabs = [
					Paths.Subtitles,
					Paths.ComponentConversation,
					Paths.NonCurriculumComponents,
					Paths.TitlePhraseList,
					Paths.ContentObjective,
					Paths.ContentExposure,
					Paths.REP,
					Paths.ManageChapters,
					Paths.EditManageChapters
				];
				break;
			case DisabledTabType.edit:
				this.disabledTabs = [
					Paths.ComponentConversation,
					Paths.NonCurriculumComponents,
					Paths.ManageChapters,
					Paths.EditManageChapters,
					Paths.ContentObjective,
					Paths.ContentExposure,
					Paths.REP,
					Paths.Subtitles,
					Paths.NonCurriculumComponents,
					Paths.TitlePhraseList
				];
				break;
			case DisabledTabType.textDirty:
				this.disabledTabs = [
					Paths.Subtitles,
					Paths.ComponentConversation,
					Paths.NonCurriculumComponents,
					Paths.TitlePhraseList,
					Paths.ContentObjective,
					Paths.ContentExposure,
					Paths.REP,
					Paths.ManageChapters,
					Paths.EditManageChapters
				];
				break;
			case DisabledTabType.all:
				this.disabledTabs = [
					Paths.NonCurriculumComponentsText,
					Paths.Subtitles,
					Paths.ComponentConversation,
					Paths.NonCurriculumComponents,
					Paths.TitlePhraseList,
					Paths.ContentObjective,
					Paths.ContentExposure,
					Paths.REP,
					Paths.ManageChapters,
					Paths.EditManageChapters
				];
		}
	}

	disablePath(path: string) {
		// Diable few tabs which are not currently in use
		if (Paths.NonCurriculumComponents === path) {
			return true;
		}

		if (this.disabledTabs.length) {
			return !!this.disabledTabs.find((p) => p === path);
		}
		return false;
	}

	onCreateClick() {
		// Go to text mode create
		this.updateDisabledTabs(DisabledTabType.create);
		this.titleListModule.setRevision(0);
		this.titleListModule.setEditedContentId("");
		// clear the persisted grid data
		this.titleListModule.changePersistedData();
		this.$router.push(Paths.NonCurriculumComponentsText);
	}

	onTitleClick(titleId: string) {
		this.updateDisabledTabs(DisabledTabType.edit);
		// Go to edit mode text
		this.titleListModule.setEditedContentId(titleId);
		this.setRouter(titleId);
	}

	onUnitTypeChange(e) {
		this.titleListModule.setUnitBased(e.target.value);
	}

	setRouter(titleId) {
		switch (true) {
			case isLinkVisible(VcsMenuCategory.CurriculumManagement, VcsMenuCategoryPermission.Text):
				this.$router.push(GLUtil.pathStringify(Paths.NonCurriculumComponentsEditText, { id: titleId }));
				break;
			default:
				this.$router.push(Paths.Unauthorized);
		}
	}

	onBackClick() {
		if (this.showComparer) {
			this.hideComparer();
		} else {
			this.onAddEditCancel();
		}
	}

	onAddEditCancel() {
		// go to list mode
		this.$router
			.push(Paths.NonCurriculumComponents)
			.then(() => {
				this.disabledTabs = [];
				this.titleListModule.setEditedContentId("");
				this.titleListModule.resetChildState();
				this.showComparer = false;
			})
			.catch(() => {
				// Canceled
			});
	}

	showRevise() {
		if (this.titleListModule.editedContentId.length) {
			return VersionGroupModule.versionGroups.find(
				(v) =>
					v.curriculumType === this.titleListModule.editedContentDetails.curriculumType &&
					(v.id === VersionGroupModule.selectedGsVersion || v.id === VersionGroupModule.selectedLsVersion)
			)?.isPublished;
		}
		return false;
	}

	showComparison() {
		const {
			editedContentId,
			validText,
			validForm,
			textPageDirty,
			editeContentSelectedChapterId,
			editedContentDetails,
			setRevision
		} = this.titleListModule;
		const isDisabled = editedContentId.length
			? !validText || !validForm || !textPageDirty
			: !validText || !validForm;
		const chooseFirstChapter = () => {
			if (editeContentSelectedChapterId === "all") {
				const maxRevision = max(editedContentDetails.chapters[0].contentText.map((m) => m.revision));
				setRevision(maxRevision ? maxRevision : 0);
			}
		};
		if (!isDisabled) {
			MessageHelper.Confirm(
				fmtMsg(TitleListLocale.TitleListLeaveWarn),
				() => {
					chooseFirstChapter();
					this.showComparer = true;
				},
				null
			);
		} else {
			chooseFirstChapter();
			this.showComparer = true;
		}
	}

	hideComparer(contentId?: string, force = false) {
		if ((contentId && contentId !== this.titleListModule.editedContentId) || (contentId && force)) {
			!force && this.$router.push(GLUtil.pathStringify(Paths.NonCurriculumComponentsEditText, { id: contentId }));
			this.titleListModule
				.fetchContentDetails(contentId)
				.then(() => force && this.titleListModule.setForceProcess(true));
			setTimeout(() => {
				this.showComparer = false;
				Vue.nextTick(() => this.addChildWidthChangeEventListener());
			}, 50);
		} else {
			this.titleListModule.setTextState();
			this.showComparer = false;
			Vue.nextTick(() => this.addChildWidthChangeEventListener());
		}
		this.updateDisabledTabs(DisabledTabType.edit);
	}

	// returns the request object for creation of title (component)
	createAddRequest(
		formDetails: CurrentContentDetailsForm,
		textDetails: ContentTextEditModel[],
		wordsToProcess: string[],
		addNewWords: boolean
	) {
		const request: CreateContentRequestModel = {
			...formDetails,
			contentText: textDetails.map((d, index) => {
				const text = { ...d };
				delete text.previousText;
				delete text.id;
				text.sequence = index + 1;
				return text as EditContentTextRequestModel;
			})
		};
		const model = request as any;
		model.wordsToProcess = wordsToProcess;
		model.addNewWords = addNewWords;

		delete model.categoryId; // since categoryId is not saved with content
		let versionId = "";
		if(this.titleListModule.isunitBased === NonCurriculumComponentType.Unit) {
			const selectedLevel = this.titleListModule.levels.find((l) => l.id === model.vcsLevelId);
			versionId =
				selectedLevel?.curriculumType === CurriculumType.GrapeSEED
					? VersionGroupModule.selectedGsVersion
					: VersionGroupModule.selectedLsVersion;

			model.unit = selectedLevel?.unit;
		}
		else {
			versionId = this.getVersionIdForNonCurriculumComponents();
			model.vcsLevelId = null;
			model.unit = null;
		}
		return { model, versionId };
	}

	// calls method to add a new title
	executeAddRequest(model: any, versionId: string) {
		const resetSaving = () => {
			this.saving = false;
			this.titleListModule.setSavingTitle(false);
		};

		const enterEditMode = (contentId: string) => {
			!this.titleListModule.validText && this.titleListModule.setValidText(true);
			!this.titleListModule.validForm && this.titleListModule.setValidForm(true);
			this.titleListModule.formTouched && this.titleListModule.setFormTocuched(false);
			this.titleListModule.textPageDirty && this.titleListModule.togglePageDirty();
			this.updateDisabledTabs(DisabledTabType.edit);
			this.$router
				.push(GLUtil.pathStringify(Paths.NonCurriculumComponentsEditText, { id: contentId }))
				.then(() => this.titleListModule.setSavingTitle(false));
			this.saving = false;

			// after save set old content to updated content
			this.oldTextLines = this.newTextLines;
		};

		ContentService.createContent(model, versionId)
			.then((contentId) => {
				enterEditMode(contentId);
				const ids: string[] = [];
				if (contentId) {
					ids.push(contentId);
				}
				if (model.contentId) {
					ids.push(model.contentId);
				}
				MessageHelper.Message(NotificationType.Success, fmtMsg(TitleListLocale.TitleListCreateTitleSuc));
			})
			.catch(() => {
				MessageHelper.Message(NotificationType.Failed, fmtMsg(TitleListLocale.TitleListCreateTitleFail));
				resetSaving();
			});
	}

	async onSubmit(revise = false) {
		const onOk = (formDetails, textDetails, revise, wordsToProcess, addNewWords) => {
			if (this.titleListModule.editedContentId.length) {
				// edit
				const request: EditContentRequestModel = {
					id: this.titleListModule.editedContentDetails.id,
					...formDetails,
					contentText: textDetails.map((d, index) => {
						const text = { ...d };
						delete text.previousText;
						text.sequence = index + 1;
						return text as EditContentTextRequestModel;
					})
				};
				const model = request as any;
				model.wordsToProcess = wordsToProcess;
				model.addNewWords = addNewWords;
				delete model.categoryId;
				this.saving = true;
				this.titleListModule.setSavingTitle(true);
				let versionId = "";
				if(this.titleListModule.isunitBased === NonCurriculumComponentType.Unit) {
					const selectedLevel = this.titleListModule.levels.find((l) => l.id === model.vcsLevelId);
					versionId =
						selectedLevel?.curriculumType === CurriculumType.GrapeSEED
							? VersionGroupModule.selectedGsVersion
							: VersionGroupModule.selectedLsVersion;

					model.unit = selectedLevel?.unit;
				}
				else {
					versionId = this.getVersionIdForNonCurriculumComponents();
					model.vcsLevelId = null;
					model.unit = null;
				}

				if (revise) {
					this.titleListModule.setRevision(this.titleListModule.currentRevision + 1);
				}

				ContentService.editContent(model, this.titleListModule.currentRevision, revise, versionId)
					.then(async () => {
						const ids: string[] = [];
						if (model.chapterId) {
							ids.push(model.chapterId);
						} else {
							ids.push(model.id);
						}
						this.titleListModule.fetchContentDetails(model.id).then(() => {
							const details = this.titleListModule.editedContentDetails;
							this.titleListModule.setForceProcess(true);
							MessageHelper.Message(
								NotificationType.Success,
								fmtMsg(TitleListLocale.TitleListEditTitleSuc)
							);
							this.updateDisabledTabsToManage();
							this.titleListModule.setRevision(this.titleListModule.editedContentDetails.revisions);
							this.titleListModule.setSavingTitle(false);
							this.saving = false;

							// after save set old content to updated content
							this.oldTextLines = this.newTextLines;
						});
					})
					.catch(() => {
						MessageHelper.Message(NotificationType.Failed, fmtMsg(TitleListLocale.TitleListEditTitleFail));
						this.saving = false;
						this.titleListModule.setSavingTitle(false);
					});
			} else {
				// create mode
				const { model, versionId } = this.createAddRequest(
					formDetails,
					textDetails,
					wordsToProcess,
					addNewWords
				);
				this.saving = true;
				this.titleListModule.setSavingTitle(true);
				this.executeAddRequest(model, versionId);
			}
		};

		const form: any = this.$refs.form;
		const formDetails: CurrentContentDetailsForm = await form.getFormDetails();
		const text: any = this.$refs.nav;
		const textInfo:
			| { initialTextDetails: ContentTextModel[]; textDetails: ContentTextEditModel[] }
			| boolean = text.getTextDetails();

		// find the difference between old text and new text and show to user
		if (typeof formDetails !== "boolean" && typeof textInfo !== "boolean") {
			if ((!this.oldTextLines || this.oldTextLines.length == 0) && this.titleListModule.editedContentId.length) {
				this.oldTextLines = textInfo.initialTextDetails.map((m) => m.textContent);
			}

			this.newTextLines = textInfo.textDetails.map((item: ContentTextEditModel) => {
				return item.textContent;
			});
			const allMismatchedData = getUniqueChangedWords(this.oldTextLines, this.newTextLines);
			const wordsToCalculate = allMismatchedData.allMismatched;

			onOk(formDetails, textInfo.textDetails, revise, wordsToCalculate, true);
		}
	}

	async execUpdateTitleInfo() {
		const form: any = this.$refs.form;
		const formDetails: CurrentContentDetailsForm = await form.getFormDetails();
		if (typeof formDetails === "boolean") {
			return;
		}

		// save to db
		this.saving = true;
		this.titleListModule.setSavingTitle(true);

		const textDetails = this.titleListModule.editedContentDetails.contentText;
		const model: EditContentRequestModel & any = {
			id: this.titleListModule.editedContentDetails.id,
			...formDetails,
			contentText: textDetails
		};
		delete model.categoryId;
		let versionId = "";
		if(this.titleListModule.isunitBased === NonCurriculumComponentType.Unit) {
			const selectedLevel = this.titleListModule.levels.find((l) => l.id === model.vcsLevelId);
			versionId =
				selectedLevel?.curriculumType === CurriculumType.GrapeSEED
					? VersionGroupModule.selectedGsVersion
					: VersionGroupModule.selectedLsVersion;

			model.unit = selectedLevel?.unit;
		}
		else {
			versionId = this.getVersionIdForNonCurriculumComponents();
			model.unit = null;
			model.vcsLevelId = null;
		}
		const revision = max(textDetails.map((m) => m.revision));
		ContentService.editContent(model, revision ? revision : 0, false, versionId)
			.then(() => {
				// refetch details
				this.titleListModule.fetchContentDetails(model.id).then(() => {
					MessageHelper.Message(NotificationType.Success, fmtMsg(TitleListLocale.TitleListEditTitleSuc));
					this.saving = false;
					this.titleListModule.setSavingTitle(false);
				});
			})
			.catch(() => {
				MessageHelper.Message(NotificationType.Failed, fmtMsg(TitleListLocale.TitleListEditTitleFail));
				this.saving = false;
				this.titleListModule.setSavingTitle(false);
			});
	}

	getVersionIdForNonCurriculumComponents()  {
		let versionId="";
		// if both versions are selected, then return GS
		if(isAnyVersionSelected() && VersionGroupModule.versionGroups.length === 2) {
			versionId = this.versionGroupModule.selectedGsVersion
		}
		else {
			if (this.versionGroupModule.selectedGsVersion === "-1") {
				versionId = this.versionGroupModule.selectedLsVersion
			}
			else {
				versionId = this.versionGroupModule.selectedGsVersion
			}
		}
		return versionId;
	}
}
